/**
 * Endringslogg (#267 SET-32): who changed which company setting, when, and from what to what, newest
 * first, 50 at a time (GET /settings/history). Applied changes only: a refused or failed save writes no
 * row. Covers the company row (PUT /tenant/:id); pay types, rate rules, employer costs, staffing, roles
 * and the workspace switches are saved elsewhere and are not in it, which the panel says.
 */
import { get } from '@/api/client';
import { T } from '@/i18n';
import { Button, Empty, LoadError, Panel, Skeleton } from '@/ui';
import { useInfiniteQuery } from '@tanstack/react-query';

export interface SettingChange {
  id: number;
  field: string;
  before: unknown;
  after: unknown;
  at: string;
  who: string | null;
}

/** the field as the page names it; an unknown field shows its key rather than nothing */
const FIELD: Record<string, string> = {
  name: 'Navn',
  org_number: 'Organisasjonsnummer',
  tariff_bound: 'Tariffavtale',
  subscription_type: 'Abonnement',
  availability_requires_approval: 'Tilgjengelighet må godkjennes',
  projects_required: 'Prosjekt påkrevd på vakter',
  projects_label: 'Ordet for prosjekt',
  budget_warn_percent: 'Varsel ved budsjett, prosent',
  is_gps_enabled: 'GPS på stempling',
  gps_radius_meters: 'Radius, meter',
  latitude: 'Breddegrad',
  longitude: 'Lengdegrad',
  shift_length_hours: 'Avtalt daglig arbeidstid',
  agreed_week_hours: 'Avtalt ukentlig arbeidstid',
  overtime_period_start: 'Start på 52-ukersperioden',
  opening_hours: 'Åpningstider',
  break_mode: 'Pause',
  lunch_included: 'Pause betalt',
  lunch_length_minutes: 'Pauselengde, minutter',
  vacation_day_basis: 'Feriedager telles som',
  payroll_system: 'Lønnssystem',
  regular_pay_code: 'Lønnsart for vanlige timer',
  hoytid_pay_code: 'Lønnsart for tillegg 1. og 17. mai',
  exempt_salaried_no_supplements: 'Ingen tillegg for fastlønte unntatt arbeidstidsreglene',
  merarbeid_plain_pay: 'Merarbeid betales som ordinær lønn',
  avspasering_unused: 'Ubrukte timer',
  avspasering_payout_day: 'Utbetalingsdato for ubrukte timer',
  avspasering_payout_code: 'Lønnsart for utbetalt avspasering',
  push_settings: 'Varsler',
};
const BREAK: Record<string, string> = {
  auto: 'trekkes alltid',
  punched: 'trekkes når den stemples',
  punched_min: 'stemplet, med fast lengde som minimum',
};

export function valueText(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return T('tom');
  if (typeof v === 'boolean') return v ? T('Ja') : T('Nei');
  if (field === 'break_mode' && typeof v === 'string') return T(BREAK[v] ?? v);
  if (typeof v === 'object') return T('endret');
  return String(v);
}

const when = (iso: string) =>
  new Intl.DateTimeFormat('nb-NO', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Oslo',
  }).format(new Date(iso));

export default function Endringer() {
  const q = useInfiniteQuery({
    queryKey: ['settings-history'],
    queryFn: ({ pageParam }) =>
      get<{ events: SettingChange[]; next: number | null }>(
        `/settings/history${pageParam ? `?before=${pageParam}` : ''}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const events = q.data?.pages.flatMap((p) => p.events) ?? [];
  return (
    <Panel
      title={T('Endringslogg')}
      sub={T(
        'Endringer i bedriftens innstillinger, med hvem og når. Lønnsarter, satser, arbeidsgiverkostnad, bemanning, roller og arbeidsmåte lagres andre steder og står ikke her.',
      )}
    >
      {q.isPending ? (
        <Skeleton rows={4} />
      ) : q.isError ? (
        <LoadError what={T('endringsloggen')} onRetry={() => q.refetch()} />
      ) : !events.length ? (
        <Empty title={T('Ingen endringer ennå')} />
      ) : (
        <>
          <ol className="stlog" data-testid="settings-history">
            {events.map((e) => (
              <li key={e.id}>
                <b>{FIELD[e.field] ? T(FIELD[e.field] as string) : e.field}</b>
                <span>
                  {valueText(e.field, e.before)} → {valueText(e.field, e.after)}
                </span>
                <small>
                  {e.who ?? T('Ukjent bruker')}, {when(e.at)}
                </small>
              </li>
            ))}
          </ol>
          {q.hasNextPage && (
            <Button onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
              {T('Vis eldre')}
            </Button>
          )}
        </>
      )}
    </Panel>
  );
}
